// onboardingLearningPlanSteps.tsx
import { Box, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OnboardingTourStep } from "@gfazioli/mantine-onboarding-tour";
import { IconPlus } from "../shared/svg/AddIcon";

export const useOnboardingLearningPlanSteps = (): OnboardingTourStep[] => {
  const { t } = useTranslation();

  return [
    {
      id: "select-calendar",
      title: t("onboarding.selectCalendar.title"),
      content: t("onboarding.selectCalendar.content"),
    },
    {
      id: "add-calendar",
      title: t("onboarding.addCalendar.title"),
      content: t("onboarding.addCalendar.content"),
    },
    {
      id: "edit-calendar",
      title: t("onboarding.editCalendar.title"),
      content: t("onboarding.editCalendar.content"),
    },
    {
      id: "calendar-view",
      title: t("onboarding.calendarView.title"),
      content: t("onboarding.calendarView.content"),
    },
    {
      id: "calendar-period",
      title: t("onboarding.calendarPeriod.title"),
      content: t("onboarding.calendarPeriod.content"),
    },
    {
      id: "calendar-goal",
      title: t("onboarding.calendarGoal.title"),
      content: (
        <Text>
          {t("onboarding.calendarGoal.contentFirst")}
          <Box
            component="span"
            w={22}
            h={22}
            mx={4}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              backgroundColor: "rgb(233, 236, 239)",
              color: "rgb(73, 80, 87)",
              verticalAlign: "middle",
            }}
          >
            <IconPlus />
          </Box>
          {t("onboarding.calendarGoal.contentSecond")}
        </Text>
      ),
    },
    {
      id: "calendar-add-goal",
      title: t("onboarding.addGoal.title"),
      content: t("onboarding.addGoal.content"),
    },
    {
      id: "calendar-tasks",
      content: (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: "-18px",
          }}
        >
          <Text fz="20px" fw={700}>
            {t("onboarding.tasks.title")}
          </Text>
          <Text>
            {t("onboarding.tasks.contentFirst")}
            <Box
              component="span"
              w={22}
              h={22}
              mx={4}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                backgroundColor: "rgb(233, 236, 239)",
                color: "rgb(73, 80, 87)",
                verticalAlign: "middle",
              }}
            >
              <IconPlus />
            </Box>
            {t("onboarding.tasks.contentSecond")}
          </Text>
        </Box>
      ),
    },
  ];
};

export const useOnboardingReportSteps = (): OnboardingTourStep[] => {
  const { t } = useTranslation();

  const steps: OnboardingTourStep[] = [
    {
      id: "select-report",
      content: (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: "-18px",
          }}
        >
          <Text fz="20px" fw={700}>
            {t("onboarding.selectReport.title")}
          </Text>
          <Text>{t("onboarding.selectReport.content")}</Text>
        </Box>
      ),
    },
    {
      id: "button-download-pdf",
      content: (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: "-18px",
          }}
        >
          <Text fz="20px" fw={700}>
            {t("onboarding.downloadPdf.title")}
          </Text>
          <Text>{t("onboarding.downloadPdf.content")}</Text>
        </Box>
      ),
    },
    {
      id: "button-delete-report",
      content: (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: "-18px",
          }}
        >
          <Text fz="20px" fw={700}>
            {t("onboarding.deleteReport.title")}
          </Text>
          <Text>{t("onboarding.deleteReport.content")}</Text>
        </Box>
      ),
    },
    {
      id: "button-edit-section",
      content: (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: "-18px",
          }}
        >
          <Text fz="20px" fw={700}>
            {t("onboarding.editSection.title")}
          </Text>
          <Text>{t("onboarding.editSection.content")}</Text>
        </Box>
      ),
    },
    {
      id: "recommended-courses-section",
      content: (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: "-18px",
          }}
        >
          <Text fz="20px" fw={700}>
            {t("onboarding.recommendedCourses.title")}
          </Text>
          <Text>{t("onboarding.recommendedCourses.content")}</Text>
        </Box>
      ),
    },
  ];

  return steps;
};

export const useOnboardingProfileSteps = (): OnboardingTourStep[] => {
  const { t } = useTranslation();

  const steps: OnboardingTourStep[] = [
    {
      id: "profile-info",
      content: (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: "-18px",
          }}
        >
          <Text fz="20px" fw={700}>
            {t("onboarding.profileInfo.title")}
          </Text>
          <Text>{t("onboarding.profileInfo.content")}</Text>
        </Box>
      ),
    },
    {
      id: "profile-edit",
      content: (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: "-18px",
          }}
        >
          <Text fz="20px" fw={700}>
            {t("onboarding.profileEdit.title")}
          </Text>
          <Text>{t("onboarding.profileEdit.content")}</Text>
        </Box>
      ),
    },
    {
      id: "profile-cv",
      content: (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: "-18px",
          }}
        >
          <Text fz="20px" fw={700}>
            {t("onboarding.profileCv.title")}
          </Text>
          <Text>{t("onboarding.profileCv.content")}</Text>
        </Box>
      ),
    },
    {
      id: "profile-delete",
      content: (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: "-18px",
          }}
        >
          <Text fz="20px" fw={700}>
            {t("onboarding.profileDelete.title")}
          </Text>
          <Text>{t("onboarding.profileDelete.content")}</Text>
        </Box>
      ),
    },
  ];

  return steps;
};

export const useOnboardingDashboardSteps = (): OnboardingTourStep[] => {
  const { t } = useTranslation();

  return [
    {
      id: "dashboard-statistic",
      title: t("onboarding.dashboardStatistic.title"),
      content: t("onboarding.dashboardStatistic.content"),
    },
    {
      id: "dashboard-goals",
      title: t("onboarding.dashboardGoals.title"),
      content: t("onboarding.dashboardGoals.content"),
    },
    {
      id: "dashboard-recommended",
      title: t("onboarding.dashboardRecommended.title"),
      content: t("onboarding.dashboardRecommended.content"),
    },
  ];
};

export const useOnboardingCoursesSteps = (): OnboardingTourStep[] => {
  const { t } = useTranslation();

  return [
    {
      id: "courses-tab",
      content: (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: "-18px",
          }}
        >
          <Text fz="20px" fw={700}>
            {t("onboarding.courses-tab.title")}
          </Text>
          <Text>{t("onboarding.courses-tab.content")}</Text>
        </Box>
      ),
    },
    {
      id: "courses-search",
      content: (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: "-18px",
          }}
        >
          <Text fz="20px" fw={700}>
            {t("onboarding.courses-search.title")}
          </Text>
          <Text>{t("onboarding.courses-search.content")}</Text>
        </Box>
      ),
    },
    {
      id: "courses-filter",
      content: (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: "-18px",
          }}
        >
          <Text fz="20px" fw={700}>
            {t("onboarding.courses-filter.title")}
          </Text>
          <Text>{t("onboarding.courses-filter.content")}</Text>
        </Box>
      ),
    },
    {
      id: "courses-sort",

      content: (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: "-18px",
          }}
        >
          <Text fz="20px" fw={700}>
            {t("onboarding.courses-sort.title")}
          </Text>
          <Text>{t("onboarding.courses-sort.content")}</Text>
        </Box>
      ),
    },
    {
      id: "courses-card",
      content: (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: "-18px",
          }}
        >
          <Text fz="20px" fw={700}>
            {t("onboarding.courses-card.title")}
          </Text>
          <Text>{t("onboarding.courses-card.content")}</Text>
        </Box>
      ),
    },
  ];
};
